import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PutAlbumVideosResult,
  PutAlbumVideosOptions,
  PatchAlbumVideosResult,
  PatchAlbumVideosOptions,
  GetAlbumVideosOptions,
  GetAlbumVideosResult,
  getAlbumVideos,
  PutAlbumVideosFields,
  putAlbumVideos,
  PatchAlbumVideosFields,
  patchAlbumVideos,
  GetAlbumVideosFields,
} from '../fetchers/album/videos';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetAlbumVideosOptions<Fields extends GetAlbumVideosFields> = GetAlbumVideosOptions<Fields>;

/**
 * Get all the videos in a showcase
 * This method returns every video in the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function useGetAlbumVideos<Fields extends GetAlbumVideosFields>(
  getOptions: UseGetAlbumVideosOptions<Fields> | (() => UseGetAlbumVideosOptions<Fields> | null),
  config?: SWRConfiguration<GetAlbumVideosResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/albums/${options.where.albumId}/videos${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getAlbumVideos({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumVideos, {
    endpoint: '/albums/:albumId/videos',
    method: 'GET',
  });
}

/**
 * Get all the videos in a showcase
 * This method returns every video in the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function useGetAlbumVideosLazy<Fields extends GetAlbumVideosFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetAlbumVideosResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetAlbumVideosOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}/videos${serializeQuery(options)}`,
          getAlbumVideos({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumVideosLazy, {
    endpoint: '/albums/:albumId/videos',
    method: 'GET',
  });
}

type UseGetAlbumVideosOptionsInfinite<Fields extends GetAlbumVideosFields> =
  GetAlbumVideosOptions<Fields>;

/**
 * Get all the videos in a showcase
 * This method returns every video in the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function useGetAlbumVideosInfinite<Fields extends GetAlbumVideosFields>(
  getOptions:
    | UseGetAlbumVideosOptionsInfinite<Fields>
    | (() => UseGetAlbumVideosOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetAlbumVideosResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (pageIndex: number, previousPageData: GetAlbumVideosResult<Fields> | null) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/albums/${options.where.albumId}/videos?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getAlbumVideos({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumVideosInfinite, {
    endpoint: '/albums/:albumId/videos',
    method: 'GET',
  });
}

type UsePutAlbumVideosOptions<Fields extends PutAlbumVideosFields> = PutAlbumVideosOptions<Fields>;

/**
 * Replace all the videos in a showcase
 * This method replaces all the videos in the specified showcase with a new set of one or more videos. The authenticated user must be the owner of the showcase.
 */

export function usePutAlbumVideos<Fields extends PutAlbumVideosFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PutAlbumVideosResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePutAlbumVideosOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await putAlbumVideos({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePutAlbumVideos, {
    endpoint: '/albums/:albumId/videos',
    method: 'PUT',
  });
}

type UsePatchAlbumVideosOptions<Fields extends PatchAlbumVideosFields> =
  PatchAlbumVideosOptions<Fields>;

/**
 * Add, remove, or change the order of multiple videos in a showcase
 * This method can do any of the following: add multiple videos to the specified showcase, remove multiple videos from the showcase, or change the order of the videos in the showcase, depending on the body parameter. Use the **remove** parameter to remove videos and the **set** parameter to add new videos or reorder existing videos.
 *
 * Normally, the API appends videos to the showcase in ascending order, starting from position value `1` (for a newly created showcase).
 *
 * You can perform all three operations with the same request. The API processes the **remove** operation first and ignores any **set** operation that assigns a video to a position that's already occupied.
 *
 * The authenticated user must be the owner of the showcase.
 */

export function usePatchAlbumVideos<Fields extends PatchAlbumVideosFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchAlbumVideosResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchAlbumVideosOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await patchAlbumVideos({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchAlbumVideos, {
    endpoint: '/albums/:albumId/videos',
    method: 'PATCH',
  });
}
